import VenueModels from "../components/site/VenueModels";
import ReneContact from "../components/site/ReneContact";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, Globe } from "lucide-react";
import { useApi, useSeo, fileUrl } from "../lib/api";
import { ArtCard, EventCard, Loading, Page, PageTitle, Empty, MapButton, WhatsAppButton, Prose } from "../components/site/Bits";

function useRedirect(data, base) {
  const nav = useNavigate();
  useEffect(() => {
    if (data?.redirect) nav(`${base}/${data.redirect}`, { replace: true });
  }, [data, nav, base]);
}

export function Artists() {
  const { data, loading } = useApi("/public/artists");
  useSeo("Künstler", "Künstlerinnen und Künstler der verteilten Galerie.");
  if (loading) return <Loading />;
  return (
    <Page>
      <PageTitle eyebrow="Künstlerübersicht" title="Künstler">Du malst, zeichnest oder druckst? <Link to="/bewerbung/kuenstler" className="underline font-semibold" data-testid="artist-apply-link">Bewirb dich hier.</Link></PageTitle>
      {!data?.length ? <Empty>Noch keine Künstlerprofile veröffentlicht.</Empty> : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.map((a) => (
          <Link key={a.id} to={`/kuenstler/${a.slug}`} className="group bg-white border border-[#E6DFD5] p-6 hover:border-[#121212] transition-colors" data-testid={`artist-card-${a.slug}`}>
            <div className="passe aspect-square relative mb-4">{a.portrait ? <img src={fileUrl(a.portrait.thumb)} alt={a.portrait.alt || a.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" /> : <span className="absolute inset-0 grid place-items-center font-display font-black text-6xl text-[#E6DFD5]">{a.name[0]}</span>}</div>
            <p className="font-display font-black text-2xl group-hover:text-[#0047FF]">{a.name}</p><p className="text-sm text-[#6E6B65] mt-1">{a.short_bio}</p><p className="text-xs mt-2 font-semibold">{a.work_count} Werk(e)</p>
          </Link>))}</div>
      )}
    </Page>
  );
}

export function ArtistDetail() {
  const { slug } = useParams();
  const { data: a, loading, error } = useApi(`/public/artists/${slug}`);
  useRedirect(a, "/kuenstler");
  useSeo(a?.name, a?.short_bio);
  if (loading || a?.redirect) return <Loading />;
  if (error) return <Page><Empty>Profil nicht gefunden.</Empty></Page>;
  const socials = Object.entries(a.socials || {}).filter(([, v]) => v);
  return (
    <Page>
      <div className="grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-start gap-8 lg:gap-12 mb-16">
        <div className="passe aspect-[4/5] relative min-w-0 overflow-hidden">{a.portrait ? <img src={fileUrl(a.portrait.url)} alt={a.portrait.alt || a.name} className="absolute inset-0 w-full h-full object-cover" /> : <span className="absolute inset-0 grid place-items-center font-display font-black text-8xl text-[#E6DFD5]">{a.name[0]}</span>}</div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0047FF] mb-3">Künstlerprofil</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="artist-name">{a.name}</h1>
          <p className="mt-4 text-lg">{a.short_bio}</p>
          <div className="mt-6"><Prose text={a.description} /></div>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm font-semibold">
            {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-2 border border-[#E6DFD5] px-3 py-2 hover:border-[#121212]"><Globe size={14} />Website</a>}
            {socials.map(([k, v]) => <a key={k} href={v} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center border border-[#E6DFD5] px-3 py-2 hover:border-[#121212] break-words">{k}</a>)}
            {a.discover_url && <a href={a.discover_url} target="_blank" rel="noreferrer" className="link-u font-bold" data-testid="artist-discover">Entdecken · {a.discover_label || "Mehr"} ↗</a>}
            {a.public_email && <a href={`mailto:${a.public_email}`} className="link-u">{a.public_email}</a>}
          </div>
          {(a.is_operator || a.name === "René Ghavami") && <ReneContact />}
        </div>
      </div>
      <h2 className="text-2xl sm:text-3xl font-black mb-8">Werke</h2>
      {!a.works.length ? <Empty>Noch keine veröffentlichten Werke.</Empty> : <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="artist-works">{a.works.map((w) => <ArtCard key={w.id} a={w} />)}</div>}
      {a.prints.length > 0 && <p className="mt-8 text-sm"><Link to={`/prints?artist=${a.slug}`} className="underline font-semibold">{a.prints.length} Werk(e) auch als Print →</Link></p>}
      {a.events.length > 0 && <><h2 className="text-2xl font-black mt-16 mb-6">Veranstaltungen</h2><div className="grid md:grid-cols-2 gap-4">{a.events.map((e) => <EventCard key={e.id} e={{ ...e, state: e.status }} />)}</div></>}
    </Page>
  );
}

export function Venues() {
  const { data, loading } = useApi("/public/venues");
  useSeo("Ausstellungsorte", "Kunst in Lokalen, Ordinationen, Kanzleien, Hotels, Büros und Musterwohnungen entdecken oder für eigene Räume anfragen.");
  if (loading) return <Loading />;
  return (
    <Page>
      <PageTitle eyebrow="Kunst vor Ort" title="Ausstellungsorte">Kunst für Lokale, Ordinationen, Kanzleien, Hotels, Büros, Studios und Musterwohnungen. <Link to="/bewerbung/lokal" className="underline font-semibold" data-testid="venue-apply-link">Kunst für eure Räume anfragen →</Link></PageTitle>
      <details className="border rounded-xl p-5 mb-8"><summary className="font-bold cursor-pointer">Unser Angebot · Ausstellung, Kunstmiete & Bilderwechsel</summary><div className="mt-4"><VenueModels /><Link className="btn-ink inline-block px-5 py-3 mt-5 font-semibold" to="/bewerbung/lokal">Unverbindlich anfragen</Link></div></details>
      {!data?.length ? <Empty>Noch keine Lokale veröffentlicht.</Empty> : (
        <div className="grid gap-8 md:grid-cols-2">{data.map((v) => (
          <Link key={v.id} to={`/lokale/${v.slug}`} className="group block" data-testid={`venue-card-${v.slug}`}>
            <div className="aspect-[16/10] bg-[#121212] relative overflow-hidden">{v.images?.[0] && <img src={fileUrl(v.images[0].url)} alt={v.images[0].alt || v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />}</div>
            <p className="font-display font-black text-3xl mt-4 group-hover:text-[#0047FF]">{v.name}</p>
            <p className="text-sm text-[#6E6B65] flex items-center gap-1"><MapPin size={14} />{[v.address, v.postal_code, v.city].filter(Boolean).join(", ")} · {v.current_count} Werk(e) aktuell</p>
          </Link>))}</div>
      )}
    </Page>
  );
}

export function VenueDetail() {
  const { slug } = useParams();
  const { data: v, loading, error } = useApi(`/public/venues/${slug}`);
  useRedirect(v, "/lokale");
  useSeo(v?.name, v?.description?.slice(0, 150));
  if (loading || v?.redirect) return <Loading />;
  if (error) return <Page><Empty>Lokal nicht gefunden.</Empty></Page>;
  return (
    <Page>
      <div className="grid lg:grid-cols-2 gap-10 mb-16">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0047FF] mb-3">Lokalprofil</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="venue-name">{v.name}</h1>
          <div className="mt-6"><Prose text={v.description} /></div>
          <address className="not-italic mt-6 text-sm" data-testid="venue-address">{[v.address, [v.postal_code, v.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</address>
          {v.opening_hours && <p className="text-sm mt-2 whitespace-pre-line" data-testid="venue-hours">Öffnungszeiten: {v.opening_hours}</p>}
          <div className="mt-4 flex flex-wrap gap-4 items-center"><MapButton venue={v} />{v.website && <a href={v.website} target="_blank" rel="noreferrer" className="text-sm font-semibold link-u">Website ↗</a>}
            <WhatsAppButton area="venues" text={`Hallo! Frage zur Kunst im ${v.name}: ${window.location.origin}/lokale/${v.slug}`} testid="venue-whatsapp" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">{(v.images || []).slice(0, 4).map((m) => <div key={m.id} className="aspect-square relative bg-[#121212]"><img src={fileUrl(m.url)} alt={m.alt || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" /></div>)}</div>
      </div>
      <h2 className="text-2xl sm:text-3xl font-black mb-8">Aktuell ausgestellt</h2>
      {!v.current.length ? <Empty>Derzeit keine Werke ausgestellt.</Empty> : <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="venue-current">{v.current.map((w) => <ArtCard key={w.id} a={w} />)}</div>}
      {v.events.length > 0 && <><h2 className="text-2xl font-black mt-16 mb-6">Vernissagen hier</h2><div className="grid md:grid-cols-2 gap-4">{v.events.map((e) => <EventCard key={e.id} e={{ ...e, state: e.status }} />)}</div></>}
      {v.archive.length > 0 && (
        <section className="mt-16" data-testid="venue-archive"><h2 className="text-xl font-black mb-6 text-[#6E6B65]">Archiv: früher hier ausgestellt</h2>
          <div className="grid gap-8 grid-cols-2 lg:grid-cols-6 opacity-80">{v.archive.map((w) => <div key={w.id}><ArtCard a={{ ...w, venue: null }} /><p className="text-xs text-[#6E6B65] mt-1">{w.period?.start} – {w.period?.end}</p></div>)}</div></section>
      )}
    </Page>
  );
}

